import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Mail, Shield, Scale, ExternalLink } from "lucide-react";

/**
 * Complaints Procedure — public page required by Charity Commission good
 * practice. Sets out a four-step route for anyone who wishes to raise a
 * concern about the Ambedkarite Buddhist Global Network UK (CIO in formation).
 */
export default function Complaints() {
  return (
    <div className="bg-pearl">
      <section className="bg-navy text-pearl py-20" data-testid="complaints-hero">
        <div className="section-container max-w-4xl">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-saffron/15 border border-saffron/40 text-saffron mb-5">
            <AlertTriangle size={22} />
          </div>
          <p className="eyebrow text-gold">Governance · Accountability</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Complaints Procedure</h1>
          <p className="mt-6 text-pearl/80 max-w-2xl text-lg leading-relaxed">
            We take every concern seriously. This page sets out exactly how to
            raise a complaint, who will receive it, how long we will take to
            respond, and where to escalate if you remain unhappy.
          </p>
          <p className="mt-3 text-xs text-pearl/55">
            Charity registration in progress &middot; CIO application in preparation.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-3xl space-y-12">
          <Step
            n="1"
            title="Informal resolution"
            icon={<Mail size={18} />}
            body={
              <>
                <p className="text-slate-700 leading-relaxed">
                  Most concerns can be settled with a conversation. In the first
                  instance please email{" "}
                  <a
                    href="mailto:info@ambedkaritebuddhistglobalnetworkuk.com"
                    className="text-navy underline"
                    data-testid="complaints-email-info"
                  >
                    info@ambedkaritebuddhistglobalnetworkuk.com
                  </a>{" "}
                  describing what happened and what outcome you'd like. We aim
                  to acknowledge within <strong>three working days</strong> and
                  respond substantively within <strong>14 working days</strong>.
                </p>
              </>
            }
          />

          <Step
            n="2"
            title="Designated Safeguarding Lead"
            icon={<Shield size={18} />}
            body={
              <>
                <p className="text-slate-700 leading-relaxed">
                  Any concern relating to the welfare of a child, young person
                  or adult at risk should go directly to the Designated
                  Safeguarding Lead (DSL):{" "}
                  <a
                    href="mailto:safeguarding@ambedkaritebuddhistglobalnetworkuk.com"
                    className="text-navy underline"
                    data-testid="complaints-email-dsl"
                  >
                    safeguarding@ambedkaritebuddhistglobalnetworkuk.com
                  </a>.
                </p>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  Where there is an immediate risk of harm, contact the police
                  on <strong>999</strong>. The DSL will engage statutory
                  agencies where the law requires. See the full{" "}
                  <Link to="/safeguarding" className="text-navy underline">
                    Safeguarding policy
                  </Link>.
                </p>
              </>
            }
          />

          <Step
            n="3"
            title="Trustee board review"
            icon={<Scale size={18} />}
            body={
              <>
                <p className="text-slate-700 leading-relaxed">
                  If you remain dissatisfied after step 1 or 2, you may escalate
                  in writing to the trustee board:{" "}
                  <a
                    href="mailto:trustees@ambedkaritebuddhistglobalnetworkuk.com"
                    className="text-navy underline"
                    data-testid="complaints-email-trustees"
                  >
                    trustees@ambedkaritebuddhistglobalnetworkuk.com
                  </a>.
                </p>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  Any trustee with a personal interest in the matter will be
                  recused under our{" "}
                  <Link to="/conflict-of-interest" className="text-navy underline">
                    Conflict of Interest policy
                  </Link>. The trustees will provide a final written response
                  within <strong>28 working days</strong> of acknowledgement.
                </p>
              </>
            }
          />

          <Step
            n="4"
            title="External regulators"
            icon={<ExternalLink size={18} />}
            body={
              <>
                <p className="text-slate-700 leading-relaxed">
                  If the trustee response does not resolve the matter, you may
                  refer your complaint to the appropriate external body:
                </p>
                <ul className="mt-3 space-y-2 text-slate-700 text-sm">
                  <li>
                    <strong>Charity Commission for England and Wales</strong> —
                    serious concerns about charity governance. Visit{" "}
                    <a
                      href="https://www.gov.uk/complain-about-charity"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-navy underline"
                    >
                      gov.uk/complain-about-charity
                    </a>.
                  </li>
                  <li>
                    <strong>Information Commissioner's Office (ICO)</strong> —
                    data protection or privacy concerns. Visit{" "}
                    <a
                      href="https://ico.org.uk/make-a-complaint/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-navy underline"
                    >
                      ico.org.uk/make-a-complaint
                    </a>.
                  </li>
                  <li>
                    <strong>Local Authority Designated Officer (LADO)</strong>{" "}
                    or the police on <strong>101 / 999</strong> for safeguarding matters.
                  </li>
                </ul>
              </>
            }
          />
        </div>
      </section>

      <section className="py-12 bg-white border-t border-navy/10">
        <div className="section-container max-w-3xl">
          <p className="eyebrow">Our commitments</p>
          <h2 className="mt-2 font-display text-2xl text-navy">When you complain to us, we will:</h2>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
            <Commit>Listen, take you seriously, and not retaliate.</Commit>
            <Commit>Acknowledge in writing within three working days.</Commit>
            <Commit>Investigate proportionately and in confidence.</Commit>
            <Commit>Keep you informed of progress at every stage.</Commit>
            <Commit>Apologise and explain when we have got it wrong.</Commit>
            <Commit>Record every complaint to learn and improve.</Commit>
          </ul>
          <p className="mt-8 text-xs text-slate-500 italic">
            This procedure is reviewed annually by the trustee board. Last
            review: this document version. Next review: at the first AGM of
            the CIO.
          </p>
        </div>
      </section>
    </div>
  );
}

function Step({ n, title, icon, body }) {
  return (
    <article className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8" data-testid={`complaints-step-${n}`}>
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-saffron/15 text-saffron border border-saffron/40">
          {icon}
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-saffron">Step {n}</div>
          <h3 className="font-display text-xl text-navy">{title}</h3>
        </div>
      </div>
      <div className="mt-4">{body}</div>
    </article>
  );
}

function Commit({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-saffron shrink-0" />
      <span>{children}</span>
    </li>
  );
}
